import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageHero } from "@/components/site/PageHero";
import { MarketFinder } from "@/components/site/wheretobuy/MarketFinder";
import { Reveal, WfButton } from "@/components/site/primitives";
import { MARKETS } from "@/content/whereToBuy";

const RETAIL_TYPES = ["Farmers' Market"];
const RETAIL = MARKETS.filter((m) => RETAIL_TYPES.includes(m.type));

const HERO = {
  eyebrow: "Farmers’ markets 2026",
  title: "Meet your farmer at the market",
  lede:
    "What’s the best way to support a farmer? By buying their produce! Find World Farmers growers at markets across New England this season — look for the stall with the World Farmers banner.",
  image: "/images/wf/vlai/vlai-3534.jpg",
  imageAlt: "Bright red chili peppers ripening on the plant.",
};

const TIPS = [
  { title: "Look for the banner", body: "Every World Farmers stall flies the World Farmers banner — that’s your sign the produce came from the Flats." },
  { title: "Ask about unfamiliar crops", body: "If you see produce that looks unfamiliar, don’t hesitate to ask the farmer — they’re sure to have recommendations on how to prepare it." },
  { title: "Bring your SNAP/HIP card", body: "Our farmers accept SNAP, and purchases earn Healthy Incentives Program (HIP) benefits at participating markets." },
];

export default function FarmersMarketsPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SkipToContent />
      <StickyHeader overlay linkToHome />
      <main id="content" tabIndex={-1} className="outline-none">
        <PageHero {...HERO} testid="fm-hero" />

        <MarketFinder
          id="markets"
          items={RETAIL}
          types={RETAIL_TYPES}
          eyebrow="This season"
          title="Farmers’ markets this season"
          intro="Search by market, town, farmer, or crop and filter by region. Tap any market for the schedule, the farmer you’ll meet, directions, and the market website."
          testidPrefix="fm"
          className="bg-[var(--wf-cream)]"
        />

        {/* Market-day tips */}
        <section aria-labelledby="fm-tips-heading" className="wf-section bg-white">
          <div className="wf-container">
            <Reveal className="max-w-2xl">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-green-text)]">Market day</span>
              <h2 id="fm-tips-heading" className="mt-3 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl">Tips for shopping with our farmers</h2>
            </Reveal>
            <ul className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
              {TIPS.map((t, i) => (
                <Reveal key={t.title} delay={0.06 * i} as="li" className="h-full">
                  <div className="h-full rounded-sm border border-[var(--wf-border)] bg-[var(--wf-cream)] p-6">
                    <span className="font-serif text-3xl font-semibold text-[var(--wf-maroon)]">{i + 1}</span>
                    <h3 className="mt-3 font-serif text-lg font-semibold text-[var(--wf-ink)]">{t.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-[var(--wf-soil)]">{t.body}</p>
                  </div>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>

        {/* Cross-links */}
        <section aria-labelledby="fm-more-heading" className="bg-[var(--wf-cream)] py-16 sm:py-20 lg:py-24">
          <div className="wf-container">
            <div className="relative overflow-hidden rounded-sm bg-[var(--wf-farm-green)] px-6 py-14 text-center sm:px-10 sm:py-16">
              <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
              <div className="relative mx-auto max-w-2xl">
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[rgba(255,253,248,0.85)]">Can’t make it to market?</span>
                <h2 id="fm-more-heading" className="mt-4 font-serif text-3xl font-semibold leading-[1.1] text-white sm:text-4xl">Get a weekly share, or see what’s growing</h2>
                <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-[rgba(255,253,248,0.88)]">Join the CSA for 16 weeks of fresh vegetables, or explore the 80+ crops our farmers grow at the Flats.</p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <WfButton as={Link} to="/csa" variant="light" withArrow className="px-7" data-testid="fm-csa-button">Sign up for the CSA</WfButton>
                  <WfButton as={Link} to="/crops" className="border border-white/60 bg-white/10 px-7 text-white backdrop-blur-sm hover:bg-white/20" data-testid="fm-crops-button">Explore our crops</WfButton>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
